import { useState } from 'react'
import { Mail, Lock, User, Loader2, Eye, EyeOff, ArrowLeft, CheckCircle2, AlertCircle, X } from 'lucide-react'
import { supabase } from '../../api/supabase'
import { motion, AnimatePresence } from 'framer-motion'

function getStrength(pw) {
  if (!pw) return null
  let score = 0
  if (pw.length >= 8) score++
  if (pw.length >= 12) score++
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++
  if (/[0-9]/.test(pw)) score++
  if (/[^A-Za-z0-9]/.test(pw)) score++
  if (score <= 1) return { level: 'Weak',   color: 'bg-red-500',   text: 'text-red-400',   width: '25%'  }
  if (score === 2) return { level: 'Fair',   color: 'bg-yellow-500', text: 'text-yellow-400', width: '50%' }
  if (score === 3) return { level: 'Good',   color: 'bg-blue-500',   text: 'text-blue-400',   width: '75%' }
  return                { level: 'Strong', color: 'bg-green-500',  text: 'text-green-400',  width: '100%' }
}

export default function AuthModal({ open, onClose, defaultMode = 'signin' }) {
  const [mode, setMode]         = useState(defaultMode) // signin | signup | forgot
  const [email, setEmail]       = useState('')
  const [password, setPassword] = useState('')
  const [username, setUsername] = useState('')
  const [loading, setLoading]   = useState(false)
  const [error, setError]       = useState('')
  const [success, setSuccess]   = useState('')
  const [showPw, setShowPw]     = useState(false)

  const canClose = !!onClose
  const strength = mode === 'signup' ? getStrength(password) : null

  const reset = () => { setError(''); setSuccess(''); setEmail(''); setPassword(''); setUsername('') }
  const switchMode = (m) => { reset(); setMode(m) }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true); setError(''); setSuccess('')
    try {
      if (mode === 'signup') {
        if (password.length < 8) throw new Error('Password must be at least 8 characters')
        const { error: err } = await supabase.auth.signUp({
          email, password,
          options: { data: { username } }
        })
        if (err) throw err
        setSuccess('Check your email to confirm your account!')
      } else if (mode === 'forgot') {
        const { error: err } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`
        })
        if (err) throw err
        setSuccess('Password reset link sent — check your inbox.')
      } else {
        const { error: err } = await supabase.auth.signInWithPassword({ email, password })
        if (err) throw err
        onClose?.()
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/90 backdrop-blur-md"
        onClick={canClose ? onClose : undefined}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="relative bg-bg2 border border-border rounded-2xl p-6 w-full max-w-sm shadow-2xl z-10 max-h-[90vh] overflow-y-auto"
      >
        {canClose && (
          <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-white transition-colors">
            <X size={20} />
          </button>
        )}

        {/* Logo */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-bg3 border border-accent/30 flex items-center justify-center flex-shrink-0">
            <span className="text-accent font-jp font-bold text-xl" style={{ textShadow: '0 0 10px rgba(56,189,248,0.5)' }}>仲</span>
          </div>
          <div>
            <div className="font-display tracking-widest gradient-text" style={{ fontSize: '18px' }}>NAKAMA</div>
            <div className="text-accent/40 font-jp" style={{ fontSize: '9px', letterSpacing: '0.15em' }}>仲間 · OUR CREW</div>
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={mode}
            initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.15 }}>

            {mode === 'forgot' && (
              <button onClick={() => switchMode('signin')}
                className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white mb-4 transition-colors">
                <ArrowLeft size={13} /> Back to Sign In
              </button>
            )}

            <h2 className="text-xl font-bold mb-1">
              {mode === 'signin' ? 'Welcome back' : mode === 'signup' ? 'Join the crew' : 'Reset password'}
            </h2>
            <p className="text-sm text-gray-400 mb-5">
              {mode === 'signin'  ? 'Sign in to your nakama account'
               : mode === 'signup' ? 'Start your anime & manga journey'
               : "We'll email you a reset link"}
            </p>


            <form onSubmit={handleSubmit} className="space-y-3">
              {mode === 'signup' && (
                <div className="relative">
                  <User size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-600" />
                  <input value={username} onChange={e => setUsername(e.target.value)}
                    placeholder="Username" required
                    className="w-full bg-bg3 border border-border rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-accent transition-colors" />
                </div>
              )}

              <div className="relative">
                <Mail size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-600" />
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="Email address" required
                  className="w-full bg-bg3 border border-border rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-accent transition-colors" />
              </div>

              {mode !== 'forgot' && (
                <>
                  <div className="relative">
                    <Lock size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-600" />
                    <input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                      placeholder="Password" required
                      className="w-full bg-bg3 border border-border rounded-xl pl-10 pr-10 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-accent transition-colors" />
                    <button type="button" onClick={() => setShowPw(v => !v)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-300">
                      {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>

                  {/* Password strength bar */}
                  {mode === 'signup' && password && strength && (
                    <div className="space-y-1 px-0.5">
                      <div className="h-1 bg-bg3 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all duration-300 ${strength.color}`} style={{ width: strength.width }} />
                      </div>
                      <p className={`text-xs font-semibold ${strength.text}`}>{strength.level} password</p>
                    </div>
                  )}
                </>
              )}

              {error && (
                <div className="flex items-start gap-2 px-3 py-2 rounded-xl bg-red-500/10 border border-red-500/20">
                  <AlertCircle size={13} className="text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-400">{error}</p>
                </div>
              )}
              {success && (
                <div className="flex items-start gap-2 px-3 py-2 rounded-xl bg-accent/10 border border-accent/20">
                  <CheckCircle2 size={13} className="text-accent flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-accent">{success}</p>
                </div>
              )}

              <button type="submit" disabled={loading}
                className="w-full py-3 bg-accent text-bg font-bold rounded-xl text-sm hover:bg-accent3 transition-all flex items-center justify-center gap-2 shadow-blue-sm disabled:opacity-60 mt-1">
                {loading && <Loader2 size={15} className="animate-spin" />}
                {mode === 'signin' ? 'Sign In' : mode === 'signup' ? 'Create Account' : 'Send Reset Link'}
              </button>
            </form>

            <div className="mt-4 space-y-2.5 text-center">
              {mode === 'signin' && (
                <>
                  <button onClick={() => switchMode('forgot')}
                    className="block w-full text-xs text-gray-500 hover:text-accent transition-colors">
                    Forgot your password?
                  </button>
                  <p className="text-sm text-gray-500">
                    Not part of the crew yet?{' '}
                    <button onClick={() => switchMode('signup')} className="text-accent font-semibold hover:underline">
                      Join Nakama
                    </button>
                  </p>
                </>
              )}
              {mode === 'signup' && (
                <p className="text-sm text-gray-500">
                  Already have an account?{' '}
                  <button onClick={() => switchMode('signin')} className="text-accent font-semibold hover:underline">
                    Sign in
                  </button>
                </p>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </motion.div>
    </div>
  )
}
